import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Updater } from "use-immer";

import {
  IgButton,
  IgSelect,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { capitalizeFirstLetter } from "@intelligems/ig-utils";
import { Search } from "@mui/icons-material";
import { Grid, InputAdornment, MenuItem } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { setDialog } from "@src/store/slices/global";

import { PERSONALIZATION_ROUTES } from "../../../routes/routes";
import {
  resetPersonalizationListFilters,
  selectedPersonalizationListFilters,
  setListFilter,
} from "../../../store/slices/appSettings";
import { ExperienceListFiltersType } from "../../../types/experience";
import { mpClickCreatePersonalizationButton } from "../../../utils/mixpanel";

export const PersonalizationOverviewHeader = () => {
  const dispatch = useDispatch();
  const listFilters = useSelector(selectedPersonalizationListFilters);
  const onClear = () => {
    dispatch(resetPersonalizationListFilters());
  };
  const isAdmin = useIsAdmin();
  const shopifyFunctionsEnabled = useConfiguration<boolean>(
    "shopify_functions_enabled"
  );
  const onCheckoutScriptClick = () => {
    dispatch(
      setDialog({
        dialog: "checkoutScript",
        open: true,
        props: { isTest: false },
      })
    );
  };
  const handleSelectInputChange = (event: any) => {
    dispatch(
      setListFilter({
        type: "personalizationListFilters",
        key: "sortOptions",
        value: event.target.value,
      })
    );
  };

  return (
    <Grid item container direction={"column"} columnSpacing={1} rowSpacing={3}>
      <Grid
        item
        xs
        container
        direction={"row"}
        justifyContent={"space-between"}
        flexWrap={"nowrap"}
        alignItems={"center"}
      >
        <Grid item>
          <IgTypography
            sx={{
              letterSpacing: "0.5px",
              fontSize: "23px",
              fontWeight: 600,
            }}
          >
            Personalizations
          </IgTypography>
        </Grid>

        <Grid item>
          {isAdmin && !shopifyFunctionsEnabled?.value && (
            <IgButton
              sx={{ marginRight: "5px" }}
              igVariant={"white"}
              variant={"contained"}
              onClick={onCheckoutScriptClick}
            >
              Get Checkout Script (admin only)
            </IgButton>
          )}
          <Link
            style={{ textDecoration: "none" }}
            to={`${PERSONALIZATION_ROUTES.Personalization}/?action=new&step=0`}
            state={{
              showDetailsDialog: true,
            }}
            onClick={() => {
              mpClickCreatePersonalizationButton();
            }}
          >
            <IgButton variant="contained">New Personalization</IgButton>
          </Link>
        </Grid>
      </Grid>
      <Grid
        container
        item
        direction={"row"}
        columnSpacing={0.5}
        justifyContent={"space-between"}
      >
        <Grid item xs={9}>
          <IgTextField
            fullWidth={true}
            sx={{
              backgroundColor: "white",
            }}
            placeholder={"Enter Personalization Name or ID"}
            value={listFilters?.search || ""}
            onChange={(e) => {
              dispatch(
                setListFilter({
                  type: "personalizationListFilters",
                  key: "search",
                  value: e.target.value,
                })
              );
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search sx={{ color: "#909090" }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={2}>
          <IgSelect
            onChange={(e) => {
              handleSelectInputChange(e);
            }}
            fullWidth={true}
            value={listFilters?.sortOptions}
            renderValue={(e: any) => {
              return `Sort By: ${
                e === "createdAtTs" ? "Created Date" : capitalizeFirstLetter(e)
              }`;
            }}
          >
            <MenuItem value={"createdAtTs"} key={"createdAtTs"}>
              Created Date
            </MenuItem>
            <MenuItem value={"name"} key={"name"}>
              Name
            </MenuItem>
            <MenuItem value={"visitors"} key={"visitors"}>
              Visitors
            </MenuItem>
            <MenuItem value={"orders"} key={"orders"}>
              Orders
            </MenuItem>
            <MenuItem value={"revenue"} key={"revenue"}>
              Revenue
            </MenuItem>
            <MenuItem value={"status"} key={"status"}>
              Status
            </MenuItem>
          </IgSelect>
        </Grid>

        <Grid sx={{ marginRight: "-20px" }} item xs={1}>
          <IgButton onClick={onClear}>Clear</IgButton>
        </Grid>
      </Grid>
    </Grid>
  );
};
